import React, { Fragment } from "react";

import {
  Box,
  BoxProps,
  ButtonBase,
  Icon,
  styled,
  TypographyProps,
} from "@mui/material";
import { useSettings } from "app/hooks/useSettings";
import { NavLink } from "react-router-dom";
import { Paragraph, Span } from "../Typography";
import MatxVerticalNavExpansionPanel from "./MatxVerticalNavExpansionPanel";
import { userRoleCheck } from "app/auth/authRoles";

type ListLabelProps = {
  mode: string;
} & TypographyProps;

const ListLabel = styled(Paragraph)<ListLabelProps>(({ theme, mode }) => {
  return {
    fontSize: "12px",
    marginTop: "20px",
    marginLeft: "15px",
    marginBottom: "10px",
    textTransform: "uppercase",
    display: mode === "compact" ? "none" : "initial",
    color: theme.palette.text.secondary,
  };
});

const getExtAndIntCommon = (theme: any, mode: any) => ({
  display: "flex",
  overflow: "hidden",
  borderRadius: "4px",
  height: 44,
  whiteSpace: "pre",
  marginBottom: "8px",
  textDecoration: "none",
  justifyContent: "space-between",
  transition: "all 150ms ease-in",
  "&:hover": { background: "rgba(255, 255, 255, 0.08)" },
  "&.compactNavItem": {
    overflow: "hidden",
    justifyContent: "center !important",
  },
  "& .icon": {
    fontSize: "18px",
    paddingLeft: "16px",
    paddingRight: "16px",
    verticalAlign: "middle",
  },
  "& .customIcon": {
    fontSize: "18px",
    paddingLeft: mode === "compact" ? "16px" : "30px",
    paddingRight: "16px",
    verticalAlign: "middle",
    ".sideBarBase:hover &": {
      paddingLeft: "30px",
      transition: "padding-left 0.3s ease",
    },
  },
});

type InternalLinkProps = {
  mode: string;
} & BoxProps;

type ExternalLinkProps = {
  theme?: any;
  href?: string;
  className?: string;
  rel?: string;
  target?: string;
  children?: React.ReactNode;
  mode?: any;
};

const ExternalLink = styled("a")<ExternalLinkProps>(({ theme, ...props }) => ({
  ...getExtAndIntCommon(theme, (props as any).mode),
  color: theme?.palette.text.primary,
  whiteSpace: "pre",
})) as React.ComponentType<ExternalLinkProps>;

const InternalLink = styled(Box)<InternalLinkProps>(({ theme, ...props }) => ({
  "& a": {
    ...getExtAndIntCommon(theme, (props as any).mode),
    color: theme.palette.text.primary,
  },
  "& .navItemActive": {
    backgroundColor: "rgba(255, 255, 255, 0.16)",
  },
}));

type StyledTextProps = {
  mode?: string;
};

const StyledText = styled(Span)<StyledTextProps>(({ mode }) => ({
  fontSize: "0.875rem",
  paddingLeft: "0.8rem",
  display: "block",
}));

const BulletIcon = styled("div")(({ theme }) => ({
  padding: "2px",
  marginLeft: "24px",
  marginRight: "8px",
  overflow: "hidden",
  borderRadius: "300px",
  background: theme.palette.text.primary,
}));

const BadgeValue = styled("div")(() => ({
  padding: "1px 8px",
  overflow: "hidden",
  borderRadius: "300px",
}));

type MatxVerticalNavProps = {
  items: any[];
};

const MatxVerticalNav: React.FC<MatxVerticalNavProps> = ({ items }) => {
  const { settings } = useSettings();
  const { mode } = settings.layout1Settings.leftSidebar;

  const renderLevels = (data: any[], subItem?: boolean) => {
    return data.map((item, index) => {
      //roleCheck
      if (!userRoleCheck(item.role)) {
        return null;
      }

      if (item.type === "label")
        return (
          <ListLabel key={index} mode={mode} className="sidenavHoverShow">
            {item.label}
          </ListLabel>
        );

      if (item.children) {
        return (
          <MatxVerticalNavExpansionPanel mode={mode} item={item} key={index}>
            {renderLevels(item.children, true)}
          </MatxVerticalNavExpansionPanel>
        );
      } else if (item.type === "extLink") {
        return (
          <ExternalLink
            key={index}
            href={item.path}
            className={`${mode === "compact" && "compactNavItem"}`}
            rel="noopener noreferrer"
            target="_blank"
            mode={mode}
          >
            <ButtonBase key={item.name} name="child" sx={{ width: "100%" }}>
              {(() => {
                if (item.icon) {
                  return <Icon className="icon">{item.icon}</Icon>;
                } else {
                  return (
                    <span className="item-icon icon-text">{item.iconText}</span>
                  );
                }
              })()}
              <StyledText mode={mode} className="sidenavHoverShow">
                {item.name}
              </StyledText>
              <Box mx="auto"></Box>
              {item.badge && <BadgeValue>{item.badge.value}</BadgeValue>}
            </ButtonBase>
          </ExternalLink>
        );
      } else {
        return (
          <InternalLink key={index} mode={mode}>
            <NavLink
              to={item.path}
              className={({ isActive }) =>
                isActive
                  ? `navItemActive ${mode === "compact" && "compactNavItem"}`
                  : `${mode === "compact" && "compactNavItem"}`
              }
            >
              <ButtonBase key={item.name} name="child" sx={{ width: "100%" }}>
                {item?.icon ? (
                  <Icon
                    className={subItem ? "customIcon" : "icon"}
                    sx={{ width: 36 }}
                  >
                    {item.icon}
                  </Icon>
                ) : (
                  <Fragment>
                    <BulletIcon
                      className={`nav-bullet`}
                      sx={{
                        ...(mode === "compact" && {
                          display: "none",
                        }),
                      }}
                    />
                    <Box
                      className="nav-bullet-text"
                      sx={{
                        ml: "20px",
                        fontSize: "11px",
                        ...(mode !== "compact" && {
                          display: "none",
                        }),
                      }}
                    >
                      {item.iconText}
                    </Box>
                  </Fragment>
                )}
                <StyledText mode={mode} className="sidenavHoverShow">
                  {item.name}
                </StyledText>

                <Box mx="auto" />

                {item.badge && (
                  <BadgeValue className="sidenavHoverShow">
                    {item.badge.value}
                  </BadgeValue>
                )}
              </ButtonBase>
            </NavLink>
          </InternalLink>
        );
      }
    });
  };

  return <div className="navigation">{renderLevels(items)}</div>;
};

export default React.memo(MatxVerticalNav);
